"use client";

import { mergeProps } from "@base-ui/react/merge-props";
import { useRender } from "@base-ui/react/use-render";
import type React from "react";
import { Box, type BoxProps } from "./box";
import {
  RING_HIGHLIGHT,
  RING_RADIUS,
  type RoundedToken,
} from "./box-classes";
import { cn } from "../../lib/utils";
import type { Omittable } from "../../lib/props";

export type CardRadiusToken = RoundedToken;

/**
 * Inset for the card's own parts. `spacious` (24px) is the default and is what a
 * page panel wants. A dense card — a kanban card, a compact tile — needs 12px,
 * and with no way to ask for it those call sites bypass the parts entirely and
 * hand-roll Box padding, which is how a header edge and a footer edge inside one
 * card end up at different offsets. It travels as `data-density` so the parts
 * read it off the Card instead of each taking a prop, exactly as `data-table`
 * carries its own density.
 */
export type CardDensity = "compact" | "regular" | "spacious";

// `spacious` is the bare `p-6` on each part, which also covers a part rendered
// outside a Card. These two override it; `in-*` compiles to a zero-specificity
// `:where()`, so it is source order that lets them win.
const CARD_PART_PADDING =
  "in-data-[density=compact]:p-3 in-data-[density=regular]:p-4";

/**
 * Card is a Box wearing the card chrome. Everything structural — `surface`,
 * layout, spacing, `rounded`, `border` — is Box's, so a card that must name a
 * surface passes `surface` and gets that surface's fill, hairline color and shadow
 * from the one implementation. The only thing Card adds is the inner-highlight
 * ring and the card foreground, which are chrome, not depth.
 */
export type CardProps = BoxProps<"div"> & Omittable<{
  /** Corner rounding. Drives the outer corner and the inner highlight ring together. */
  radius?: CardRadiusToken;
  /** Inset applied to `CardHeader`/`CardPanel`/`CardFooter`. */
  density?: CardDensity;
  /**
   * Selection state — fills the card with a tint DERIVED from the brand
   * accent (`SELECTED_FILL`) and emits `data-selected` for callers and
   * tests. The tint follows the accent preset, and `--brand` is never-neutral,
   * so it stays blue under the neutral default theme. The fill holds on
   * hover, superseding `hoverBackground`'s plain hover wash.
   */
  selected?: boolean;
}>;

// The selected fill is DERIVED from the accent, not alpha over the card:
// brand at low alpha over a dark card reads muddy grey-blue, where a
// selected/open card should sit on a deep, saturated tint of the accent.
// Relative color keeps the accent's hue and pins the lightness per mode —
// pale in light (L .955, chroma cut to a whisper, so the card stays
// near-white), deep in dark (L .275, chroma at 40%: barely over the .25 card
// fill and well under the accent's saturation, so the hairlines of the chips
// and tokens inside stay visible) — so every accent preset gets its own
// tint. A browser without relative color keeps the alpha wash. The fill
// holds on hover; a card's hover cue is its own business. Named per mode
// (`not-dark:` / `dark:`) so a consumer's own per-mode edge classes merge
// beside it, not over it.
const SELECTED_FILL =
  "bg-brand/15 supports-[color:oklch(from_red_l_c_h)]:bg-(--card-selected-fill) " +
  "not-dark:[--card-selected-fill:oklch(from_var(--brand)_0.955_calc(c*0.15)_h)] " +
  "dark:[--card-selected-fill:oklch(from_var(--brand)_0.275_calc(c*0.4)_h)]";

export function Card({
  className,
  radius = "lg",
  surface,
  density = "spacious",
  selected,
  // `clip` (not `hidden`): the radius clips every full-bleed child fill — a
  // pressable row's hover wash, a table — without making the card a scroll
  // container, so `scrollIntoView` inside it scrolls the page, never the
  // frame, and sticky children keep working. Same contract as DialogPopup.
  // A child that must protrude (an overlay pill straddling the border)
  // overrides with `overflow="visible"` or a consumer className, which Box
  // merges last.
  overflow = "clip",
  ...props
}: CardProps): React.ReactElement {
  return (
    <Box
      data-density={density}
      data-selected={selected ? "" : undefined}
      overflow={overflow}
      className={cn(
        // A Card that NAMES a surface takes that surface's fill, hairline color and
        // shadow from Box and adds only the border WIDTH here. Emitting the
        // default fill alongside would defeat the named surface, because Box merges
        // `className` last — `bg-card` would win over `bg-elevated`.
        surface ? "border" : "border bg-card shadow-xs/5",
        "not-dark:bg-clip-padding text-card-foreground",
        RING_HIGHLIGHT,
        RING_RADIUS[radius],
        // Last in the merge so the fill beats both the default `bg-card` above
        // and a named surface's fill (Box merges className after its own maps).
        selected && SELECTED_FILL,
        className,
      )}
      data-slot="card"
      direction="column"
      surface={surface}
      {...props}
    />
  );
}

export function CardHeader({
  className,
  render,
  ...props
}: useRender.ComponentProps<"div">): React.ReactElement {
  const defaultProps = {
    className: cn(
      "grid auto-rows-min grid-rows-[auto_auto] items-start gap-1.5 p-6",
      CARD_PART_PADDING,
      "in-[[data-slot=card]:has(>[data-slot=card-panel])]:pb-4 has-data-[slot=card-action]:grid-cols-[1fr_auto]",
      className,
    ),
    "data-slot": "card-header",
  };

  return useRender({
    defaultTagName: "div",
    props: mergeProps<"div">(defaultProps, props),
    render,
  });
}

export function CardTitle({
  className,
  render,
  ...props
}: useRender.ComponentProps<"div">): React.ReactElement {
  const defaultProps = {
    className: cn("font-heading font-semibold text-lg leading-none", className),
    "data-slot": "card-title",
  };

  return useRender({
    defaultTagName: "div",
    props: mergeProps<"div">(defaultProps, props),
    render,
  });
}

export function CardDescription({
  className,
  render,
  ...props
}: useRender.ComponentProps<"div">): React.ReactElement {
  const defaultProps = {
    className: cn("text-muted-foreground text-sm", className),
    "data-slot": "card-description",
  };

  return useRender({
    defaultTagName: "div",
    props: mergeProps<"div">(defaultProps, props),
    render,
  });
}

export function CardAction({
  className,
  render,
  ...props
}: useRender.ComponentProps<"div">): React.ReactElement {
  const defaultProps = {
    className: cn(
      "col-start-2 row-span-2 row-start-1 inline-flex self-start justify-self-end",
      className,
    ),
    "data-slot": "card-action",
  };

  return useRender({
    defaultTagName: "div",
    props: mergeProps<"div">(defaultProps, props),
    render,
  });
}

export function CardPanel({
  className,
  render,
  ...props
}: useRender.ComponentProps<"div">): React.ReactElement {
  const defaultProps = {
    className: cn(
      "flex-1 p-6",
      CARD_PART_PADDING,
      "in-[[data-slot=card]:has(>[data-slot=card-header]:not(.border-b))]:pt-0 in-[[data-slot=card]:has(>[data-slot=card-footer]:not(.border-t))]:pb-0",
      className,
    ),
    "data-slot": "card-panel",
  };

  return useRender({
    defaultTagName: "div",
    props: mergeProps<"div">(defaultProps, props),
    render,
  });
}

export function CardFooter({
  className,
  render,
  ...props
}: useRender.ComponentProps<"div">): React.ReactElement {
  const defaultProps = {
    className: cn(
      "flex items-center p-6",
      CARD_PART_PADDING,
      "in-[[data-slot=card]:has(>[data-slot=card-panel])]:pt-4",
      className,
    ),
    "data-slot": "card-footer",
  };

  return useRender({
    defaultTagName: "div",
    props: mergeProps<"div">(defaultProps, props),
    render,
  });
}
